import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_bootstrapvue_3b15d9db from 'nuxt_plugin_bootstrapvue_3b15d9db' // Source: ./bootstrap-vue.js (mode: 'all')
import nuxt_plugin_axios_544d30a8 from 'nuxt_plugin_axios_544d30a8' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_rollbarclient_4c84a62a from 'nuxt_plugin_rollbarclient_4c84a62a' // Source: ./rollbar-client.js (mode: 'all')
import nuxt_plugin_axios_397e53b5 from 'nuxt_plugin_axios_397e53b5' // Source: ../plugins/axios.js (mode: 'all')
import nuxt_plugin_i18n_66ff12a5 from 'nuxt_plugin_i18n_66ff12a5' // Source: ../plugins/i18n.js (mode: 'all')
import nuxt_plugin_api_22834091 from 'nuxt_plugin_api_22834091' // Source: ../plugins/api.js (mode: 'all')
import nuxt_plugin_urlhelper_710de337 from 'nuxt_plugin_urlhelper_710de337' // Source: ../plugins/url-helper.js (mode: 'all')
import nuxt_plugin_formatter_782c44e1 from 'nuxt_plugin_formatter_782c44e1' // Source: ../plugins/formatter.js (mode: 'all')
import nuxt_plugin_config_709a1401 from 'nuxt_plugin_config_709a1401' // Source: ../plugins/config.js (mode: 'all')
import nuxt_plugin_tutorial_52233365 from 'nuxt_plugin_tutorial_52233365' // Source: ../plugins/tutorial.js (mode: 'all')
import nuxt_plugin_snotify_c0f2d022 from 'nuxt_plugin_snotify_c0f2d022' // Source: ../plugins/snotify.js (mode: 'all')
import nuxt_plugin_polyfill_375045d4 from 'nuxt_plugin_polyfill_375045d4' // Source: ../plugins/polyfill.js (mode: 'all')
import nuxt_plugin_careerlevel_1f192624 from 'nuxt_plugin_careerlevel_1f192624' // Source: ../plugins/career-level.js (mode: 'all')
import nuxt_plugin_routeparams_4dbd888e from 'nuxt_plugin_routeparams_4dbd888e' // Source: ../plugins/route-params.js (mode: 'all')
import nuxt_plugin_oneplacehelper_07cd5c11 from 'nuxt_plugin_oneplacehelper_07cd5c11' // Source: ../plugins/oneplace-helper.js (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"SkillsVue","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":"When rewarding digital talent, take a SkillsVue"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"}],"script":[{"src":"https:\u002F\u002Foneplace.ehr.com\u002F_layouts\u002F15\u002Fsec\u002Fophelper.js"}],"style":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_bootstrapvue_3b15d9db === 'function') {
    await nuxt_plugin_bootstrapvue_3b15d9db(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_544d30a8 === 'function') {
    await nuxt_plugin_axios_544d30a8(app.context, inject)
  }

  if (typeof nuxt_plugin_rollbarclient_4c84a62a === 'function') {
    await nuxt_plugin_rollbarclient_4c84a62a(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_397e53b5 === 'function') {
    await nuxt_plugin_axios_397e53b5(app.context, inject)
  }

  if (typeof nuxt_plugin_i18n_66ff12a5 === 'function') {
    await nuxt_plugin_i18n_66ff12a5(app.context, inject)
  }

  if (typeof nuxt_plugin_api_22834091 === 'function') {
    await nuxt_plugin_api_22834091(app.context, inject)
  }

  if (typeof nuxt_plugin_urlhelper_710de337 === 'function') {
    await nuxt_plugin_urlhelper_710de337(app.context, inject)
  }

  if (typeof nuxt_plugin_formatter_782c44e1 === 'function') {
    await nuxt_plugin_formatter_782c44e1(app.context, inject)
  }

  if (typeof nuxt_plugin_config_709a1401 === 'function') {
    await nuxt_plugin_config_709a1401(app.context, inject)
  }

  if (typeof nuxt_plugin_tutorial_52233365 === 'function') {
    await nuxt_plugin_tutorial_52233365(app.context, inject)
  }

  if (typeof nuxt_plugin_snotify_c0f2d022 === 'function') {
    await nuxt_plugin_snotify_c0f2d022(app.context, inject)
  }

  if (typeof nuxt_plugin_polyfill_375045d4 === 'function') {
    await nuxt_plugin_polyfill_375045d4(app.context, inject)
  }

  if (typeof nuxt_plugin_careerlevel_1f192624 === 'function') {
    await nuxt_plugin_careerlevel_1f192624(app.context, inject)
  }

  if (typeof nuxt_plugin_routeparams_4dbd888e === 'function') {
    await nuxt_plugin_routeparams_4dbd888e(app.context, inject)
  }

  if (typeof nuxt_plugin_oneplacehelper_07cd5c11 === 'function') {
    await nuxt_plugin_oneplacehelper_07cd5c11(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
